import { useCallback, useEffect, useRef } from 'react';
import { useDynamicHeightCallback } from '@splunk/dashboard-context';

/**
 * Manages dynamic-height reporting for a single visualization.
 *
 * Responsibilities:
 * - Wraps useDynamicHeightCallback so the viz can report its raw content
 *   height (without sibling chrome) while the framework adds siblingHeights.
 * - Caches the last raw content height so that siblingHeights changes can
 *   be re-composed and re-reported without a new measurement from the viz.
 *
 * @returns A stable callback the viz calls with its content height in pixels.
 */
export const useDynamicHeightReporter = (
    id: string,
    siblingHeights: number
): ((contentHeight: number) => void) => {
    const reportHeight = useDynamicHeightCallback(id);
    const lastContentHeightRef = useRef<number | undefined>(undefined);

    const onRequestHeight = useCallback(
        (contentHeight: number) => {
            lastContentHeightRef.current = contentHeight;
            reportHeight(contentHeight + siblingHeights);
        },
        [reportHeight, siblingHeights]
    );

    useEffect(() => {
        if (lastContentHeightRef.current === undefined) {
            return;
        }

        reportHeight(lastContentHeightRef.current + siblingHeights);
    }, [siblingHeights, reportHeight]);

    return onRequestHeight;
};
